// src/components/orders/SupplierOrdersModal.js
import React, { useState, useEffect } from 'react';
import { Modal, Select, Table, message, Spin, Space, Button, DatePicker, Tag } from 'antd';
import { 
    SearchOutlined, 
    ReloadOutlined, 
    CloseCircleOutlined,
    EyeOutlined,
    ClockCircleOutlined,
    CheckCircleOutlined,
    TruckOutlined,
    InboxOutlined,
    PrinterOutlined
} from '@ant-design/icons';
import { getAllSuppliers } from '../../services/supplierService';
import { getPedidosBySupplier } from '../../services/pedidosService';
import OrdersSummaryCards from './OrdersSummaryCards';
import './SupplierOrdersModal.css';

const { Option } = Select;
const { RangePicker } = DatePicker;

/**
 * SupplierOrdersModal Component
 * Modal to select a supplier and view their orders with summary statistics
 * 
 * Props:
 * - visible: Boolean - Modal visibility
 * - onClose: Function - Close handler
 * - preSelectedSupplierId: Number (optional) - Pre-select a supplier when opening
 * - onViewOrderDetails: Function (optional) - Callback when user clicks to view order details
 */
const SupplierOrdersModal = ({ 
    visible, 
    onClose, 
    preSelectedSupplierId = null,
    onViewOrderDetails 
}) => {
    const [suppliers, setSuppliers] = useState([]);
    const [loadingSuppliers, setLoadingSuppliers] = useState(false);
    const [selectedSupplierId, setSelectedSupplierId] = useState(null);
    
    const [supplierData, setSupplierData] = useState(null);
    const [orders, setOrders] = useState([]);
    const [loadingOrders, setLoadingOrders] = useState(false);
    
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
        total: 0,
    });

    const [filters, setFilters] = useState({
        status: null,
        dateFrom: null,
        dateTo: null,
    });

    // Load suppliers on mount
    useEffect(() => {
        if (visible) {
            fetchSuppliers();
        }
    }, [visible]);

    // Pre-select supplier if provided
    useEffect(() => {
        if (preSelectedSupplierId && visible) {
            setSelectedSupplierId(preSelectedSupplierId);
            fetchSupplierOrders(preSelectedSupplierId, 1, pagination.pageSize);
        }
    }, [preSelectedSupplierId, visible]);

    // Fetch all suppliers for dropdown
    const fetchSuppliers = async () => {
        setLoadingSuppliers(true);
        try {
            const response = await getAllSuppliers(1, 500, {});
            if (response && response.success) {
                setSuppliers(response.suppliers || []);
            }
        } catch (error) {
            console.error('Error fetching suppliers:', error);
            message.error('Error al cargar proveedores');
        } finally {
            setLoadingSuppliers(false);
        }
    };

    // Fetch orders for selected supplier
    const fetchSupplierOrders = async (supplierId, page = 1, pageSize = 20) => {
        if (!supplierId) {
            message.warning('Por favor seleccione un proveedor');
            return;
        }

        setLoadingOrders(true);
        try {
            const response = await getPedidosBySupplier(supplierId, page, pageSize, filters);
            
            if (response && response.success) {
                const { supplier, summary, orders: ordersList, pagination: paginationData } = response.data;
                
                setSupplierData({ supplier, summary });
                setOrders(ordersList || []);
                setPagination({
                    current: paginationData?.page || page,
                    pageSize: paginationData?.limit || pageSize,
                    total: paginationData?.total || 0,
                });
            } else {
                message.error('Error al cargar órdenes del proveedor');
            }
        } catch (error) {
            console.error('Error fetching supplier orders:', error);
            if (error.response?.status === 404) {
                message.error('Proveedor no encontrado');
            } else {
                message.error('Error al cargar órdenes del proveedor');
            }
            setSupplierData(null);
            setOrders([]);
        } finally {
            setLoadingOrders(false);
        }
    };

    // Handle supplier selection change
    const handleSupplierChange = (supplierId) => {
        setSelectedSupplierId(supplierId);
        setSupplierData(null);
        setOrders([]);
        setPagination({ current: 1, pageSize: 20, total: 0 });
        setFilters({ status: null, dateFrom: null, dateTo: null });
        
        if (supplierId) {
            fetchSupplierOrders(supplierId, 1, 20);
        }
    };

    // Handle table pagination change
    const handleTableChange = (paginationConfig) => {
        if (selectedSupplierId) {
            fetchSupplierOrders(
                selectedSupplierId, 
                paginationConfig.current, 
                paginationConfig.pageSize
            );
        }
    };

    // Handle filter change
    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    // Apply filters
    const handleApplyFilters = () => {
        if (selectedSupplierId) {
            fetchSupplierOrders(selectedSupplierId, 1, pagination.pageSize);
        }
    };

    // Clear filters
    const handleClearFilters = () => {
        setFilters({ status: null, dateFrom: null, dateTo: null });
        if (selectedSupplierId) {
            setTimeout(() => {
                fetchSupplierOrders(selectedSupplierId, 1, pagination.pageSize);
            }, 100);
        }
    };

    // Handle modal close
    const handleClose = () => {
        setSelectedSupplierId(null);
        setSupplierData(null);
        setOrders([]);
        setFilters({ status: null, dateFrom: null, dateTo: null });
        setPagination({ current: 1, pageSize: 20, total: 0 });
        onClose();
    };

    // Get status label
    const getStatusLabel = (status) => {
        const labels = {
            'PENDIENTE': 'Pendiente',
            'APROBADO': 'Aprobado',
            'EN_TRANSITO': 'En Tránsito',
            'PARCIAL': 'Parcial',
            'RECIBIDO': 'Recibido',
            'CANCELADO': 'Cancelado',
        };
        return labels[status] || status;
    };

    // Get status tag
    const getStatusTag = (status) => {
        const statusConfig = {
            PENDIENTE: { color: 'orange', icon: <ClockCircleOutlined /> },
            APROBADO: { color: 'blue', icon: <CheckCircleOutlined /> },
            EN_TRANSITO: { color: 'cyan', icon: <TruckOutlined /> },
            PARCIAL: { color: 'purple', icon: <InboxOutlined /> },
            RECIBIDO: { color: 'green', icon: <CheckCircleOutlined /> },
            CANCELADO: { color: 'red', icon: <CloseCircleOutlined /> },
        };
        const config = statusConfig[status] || { color: 'default', icon: null };
        return (
            <Tag color={config.color} icon={config.icon}>
                {getStatusLabel(status)}
            </Tag>
        );
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('es-AR', { 
            year: 'numeric', 
            month: '2-digit', 
            day: '2-digit' 
        });
    };

    // Format currency
    const formatPrice = (price) => {
        if (typeof price !== 'number' || isNaN(price)) return '$ 0.00';
        return `$ ${price.toLocaleString('es-AR', { 
            minimumFractionDigits: 2, 
            maximumFractionDigits: 2 
        })}`;
    };

    // Table columns
    const columns = [
        {
            title: 'N° Orden',
            dataIndex: 'order_number',
            key: 'order_number',
            width: 130,
            render: (text) => <strong>{text}</strong>,
        },
        {
            title: 'Factura Prov.',
            dataIndex: 'related_supplier_invoice',
            key: 'related_supplier_invoice',
            width: 140,
            render: (text) => text || '-',
        },
        {
            title: 'Fecha',
            dataIndex: 'order_date',
            key: 'order_date',
            width: 110,
            render: (date) => formatDate(date),
        },
        {
            title: 'Estado',
            dataIndex: 'status',
            key: 'status',
            width: 120,
            render: (status) => getStatusTag(status),
        },
        {
            title: 'Total',
            dataIndex: 'total',
            key: 'total',
            width: 120,
            align: 'right',
            render: (price) => formatPrice(price),
        },
        {
            title: 'IVA',
            dataIndex: 'iva',
            key: 'iva',
            width: 100,
            align: 'right',
            render: (price) => formatPrice(price),
        },
        {
            title: 'Descuento',
            dataIndex: 'discount',
            key: 'discount',
            width: 100,
            align: 'right',
            render: (price) => formatPrice(price),
        },
        {
            title: 'Entrega',
            dataIndex: 'actual_delivery_date',
            key: 'actual_delivery_date',
            width: 110,
            render: (date) => formatDate(date),
        },
        {
            title: 'Acciones',
            key: 'actions',
            width: 80,
            fixed: 'right',
            render: (_, record) => (
                <Button
                    type="primary"
                    size="small"
                    icon={<EyeOutlined />}
                    onClick={() => {
                        if (onViewOrderDetails) {
                            onViewOrderDetails(record.id);
                        }
                    }}
                    title="Ver detalles"
                />
            ),
        },
    ];

    return (
        <Modal
            title={
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingRight: '40px' }}>
                    <span>Órdenes por Proveedor</span>
                    {selectedSupplierId && (
                        <Button 
                            type="primary"
                            icon={<PrinterOutlined />}
                            onClick={() => {
                                window.open(`#/print-po-supplier?supplierId=${selectedSupplierId}`, '_blank');
                            }}
                            size="middle"
                        >
                            Imprimir
                        </Button>
                    )}
                </div>
            }
            open={visible}
            onCancel={handleClose}
            width={1200}
            footer={[
                <Button key="close" onClick={handleClose}>
                    Cerrar
                </Button>
            ]}
            className="supplier-orders-modal"
        >
            <div className="modal-content">
                {/* Supplier Selector */}
                <div className="supplier-selector-section">
                    <label>Seleccione un proveedor:</label>
                    <Select
                        showSearch
                        placeholder="Buscar y seleccionar proveedor"
                        style={{ width: '100%' }}
                        value={selectedSupplierId}
                        onChange={handleSupplierChange}
                        loading={loadingSuppliers}
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                    >
                        {suppliers.map(supplier => (
                            <Option key={supplier.id} value={supplier.id}>
                                {supplier.razon_social} - CUIT: {supplier.documento_nro}
                            </Option>
                        ))}
                    </Select>
                </div>

                {/* Show loading spinner or data */}
                {loadingOrders && !supplierData ? (
                    <div className="loading-container">
                        <Spin size="large" tip="Cargando órdenes del proveedor..." />
                    </div>
                ) : supplierData ? (
                    <>
                        {/* Summary Cards — adapt flat supplier summary into the { overall, byStatus } shape
                             that OrdersSummaryCards expects (same as getAllOrders response) */}
                        <OrdersSummaryCards 
                            summary={supplierData.summary ? {
                                overall: {
                                    all:           supplierData.summary.all           || { total: 0, iva: 0, count: 0 },
                                    paid_total:    supplierData.summary.paid_total    || 0,
                                    iva_paid_all:  supplierData.summary.iva_paid_all  || 0,
                                    pending_total: supplierData.summary.pending_total || 0,
                                    monthlyBreakdown: [], // not available per-supplier; hides month selector
                                },
                                byStatus: supplierData.summary.byStatus || {},
                            } : null}
                            supplierName={supplierData.supplier?.supplier_razon_social}
                            orders={orders}
                        />

                        {/* Filters */}
                        <div className="supplier-orders-filters">
                            <Space wrap size="middle">
                                <Select
                                    placeholder="Filtrar por estado"
                                    style={{ width: 180 }}
                                    allowClear
                                    value={filters.status}
                                    onChange={(value) => handleFilterChange('status', value)}
                                >
                                    <Option value="PENDIENTE">Pendiente</Option>
                                    <Option value="APROBADO">Aprobado</Option>
                                    <Option value="EN_TRANSITO">En Tránsito</Option>
                                    <Option value="PARCIAL">Parcial</Option>
                                    <Option value="RECIBIDO">Recibido</Option>
                                    <Option value="CANCELADO">Cancelado</Option>
                                </Select>

                                <RangePicker
                                    format="DD/MM/YYYY"
                                    placeholder={['Desde', 'Hasta']}
                                    onChange={(dates) => {
                                        if (dates) {
                                            handleFilterChange('dateFrom', dates[0].format('YYYY-MM-DD'));
                                            handleFilterChange('dateTo', dates[1].format('YYYY-MM-DD'));
                                        } else {
                                            handleFilterChange('dateFrom', null);
                                            handleFilterChange('dateTo', null);
                                        }
                                    }}
                                />

                                <Button 
                                    type="primary" 
                                    icon={<SearchOutlined />}
                                    onClick={handleApplyFilters}
                                >
                                    Aplicar
                                </Button>
                                <Button 
                                    icon={<ReloadOutlined />}
                                    onClick={handleClearFilters}
                                >
                                    Limpiar
                                </Button>
                            </Space>
                        </div>

                        {/* Orders Table */}
                        <div className="supplier-orders-table">
                            <Table
                                columns={columns}
                                dataSource={orders}
                                rowKey="id"
                                loading={loadingOrders}
                                pagination={pagination}
                                onChange={handleTableChange}
                                size="middle"
                                scroll={{ x: 1000 }}
                            />
                        </div>
                    </>
                ) : selectedSupplierId ? (
                    <div className="empty-state">
                        <p>No se encontraron órdenes para este proveedor</p>
                    </div>
                ) : (
                    <div className="empty-state">
                        <p>Seleccione un proveedor para ver sus órdenes</p>
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default SupplierOrdersModal;
